import { releaseFeatures } from "@/config/releaseFeatures";
import { readGuestIdentity } from "@/util/guestIdentity";
import { api } from "./api";
import { applyGuestIdentity, userStore } from "@/redux/user";
import { setLeagueGames, setTier } from "@/redux/membership";
import { FREE_TIER, VariantTypes } from "@chesshotel/constants";
import type { GuestIdentity } from "@/util/guestIdentity";

export interface GuestSnapshot extends GuestIdentity {
  id: number; persistent: true; leagueGamesRemaining: number;
  activeGameId?: string; practiceData?: Record<string, any>; leagueData?: Record<string, any>;
}
// Serialize first creation, so concurrent UI mounts cannot mint several profiles.
let bootstrap: Promise<GuestSnapshot | null> | null = null;
export function getGuestSession(identity: GuestIdentity) {
  if (!releaseFeatures.persistentGuests) return Promise.resolve(null);
  if (!bootstrap) bootstrap = api.post("/guest/session", { ...identity, returningGuest: !!readGuestIdentity() }, { timeout: 5000 })
    .then(({ data }) => data.enabled ? data.guest as GuestSnapshot : null)
    .finally(() => { bootstrap = null; });
  return bootstrap;
}
export async function renamePersistentGuest(identity: GuestIdentity) {
  try {
    const { data } = await api.post("/guest/name", identity, { timeout: 5000 });
    applyGuestSnapshot(data.guest);
    return null;
  } catch (error: any) { return error.response?.data?.error || "unavailable"; }
}
export function applyGuestSnapshot(guest?: Partial<GuestSnapshot>) {
  const current = userStore.getState();
  if (!guest || !current.isGuest || guest.id !== current.id) return;
  if (guest.name) applyGuestIdentity({ name: guest.name, generated: guest.generated ?? current.generatedGuestName,
    chosen: guest.chosen ?? !current.isDefaultGuest });
  if (Number.isInteger(guest.leagueGamesRemaining)) {
    setTier(FREE_TIER); setLeagueGames(Math.max(0, guest.leagueGamesRemaining));
  }
}
export async function restoreGuestProgress(guest: GuestSnapshot) {
  const [stats, leagues] = await Promise.all([import("@/redux/stats"), import("@/redux/leagues")]);
  if (!userStore.getState().isGuest || userStore.getState().id !== guest.id) return;
  if (guest.practiceData) {
    const empty = { elo: 1200, nrGames: 0, winsBlack: 0, winsWhite: 0, lossesBlack: 0, lossesWhite: 0, drawsBlack: 0, drawsWhite: 0 };
    const display = (variant: number) => { const value = { ...empty, ...guest.practiceData[variant] }; return { ...value, nrGames: (value.gamesWhite || 0) + (value.gamesBlack || 0) }; };
    stats.setStats({ all: display(VariantTypes.VARIANT_CHESS), chess960: display(VariantTypes.VARIANT_CHESS960) });
  }
  if (guest.leagueData) {
    leagues.resetLeagueScores();
    for (const leagueScore of Object.values(guest.leagueData)) leagues.updateLeagueScores({ leagueScore, rank: null } as any);
  }
}
